import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config/api';

export default function GlobalAlertBanner() {
  const { isDark } = useTheme();
  const { user, premiumStatus } = useAuth();
  const [alert, setAlert] = useState(null);

  const loadAlert = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/public`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      // If alert is not enabled or no message, clear the alert
      if (!res.data?.alert?.enabled || !res.data?.alert?.message) {
        setAlert(null);
        return;
      }
      
      // Get audience - can be string (legacy) or array (new multi-select)
      let audiences = res.data.alert.audience || ['all'];
      if (typeof audiences === 'string') {
        audiences = [audiences];
      }
      
      // Determine user type
      const isPremiumPaid = premiumStatus?.is_premium === true && premiumStatus?.trial_used === true;
      const isTrial = premiumStatus?.is_premium === true && premiumStatus?.trial_used === false;
      const isFree = premiumStatus?.is_premium === false || (user && !premiumStatus?.is_premium);
      
      let shouldShow = false;
      
      // Check if user matches any of the selected audiences
      if (audiences.includes('all')) {
        shouldShow = true;
      } else {
        if (audiences.includes('premium') && isPremiumPaid) {
          shouldShow = true;
        }
        if (audiences.includes('trial') && isTrial) {
          shouldShow = true;
        }
        if (audiences.includes('free') && isFree) {
          shouldShow = true;
        }
      }
      
      if (shouldShow) {
        setAlert(res.data.alert);
      } else {
        setAlert(null);
      }
    } catch (err) {
      console.log('Could not load alert settings');
    }
  }, [user, premiumStatus]);

  useEffect(() => {
    loadAlert();
    
    // Poll every 10 seconds to check for updates
    const interval = setInterval(loadAlert, 10000);
    
    // Also reload when page becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadAlert();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    
    // Reload on focus
    const handleFocus = () => loadAlert();
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadAlert]);

  if (!alert) return null;

  const config = {
    success: {
      bg: isDark ? 'bg-green-500/20' : 'bg-green-50',
      border: 'border-green-500/50',
      text: isDark ? 'text-green-200' : 'text-green-800',
      icon: CheckCircle,
      iconColor: 'text-green-500'
    },
    warning: {
      bg: isDark ? 'bg-yellow-500/20' : 'bg-yellow-50',
      border: 'border-yellow-500/50',
      text: isDark ? 'text-yellow-200' : 'text-yellow-800',
      icon: AlertTriangle,
      iconColor: 'text-yellow-500'
    },
    danger: {
      bg: isDark ? 'bg-red-500/20' : 'bg-red-50',
      border: 'border-red-500/50',
      text: isDark ? 'text-red-200' : 'text-red-800',
      icon: XCircle,
      iconColor: 'text-red-500'
    },
    info: {
      bg: isDark ? 'bg-blue-500/20' : 'bg-blue-50',
      border: 'border-blue-500/50',
      text: isDark ? 'text-blue-200' : 'text-blue-800',
      icon: Info,
      iconColor: 'text-blue-500'
    }
  };

  const style = config[alert.type] || config.info;
  const Icon = style.icon;

  return (
    <div 
      className={`mx-4 mb-4 px-4 py-3 rounded-xl flex items-center gap-3 border ${style.bg} ${style.border} animate-slideDown`}
      style={{ marginTop: '20px' }}
      data-testid="global-alert-banner"
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${style.iconColor}`} />
      <p className={`text-sm flex-1 ${style.text}`}>
        {alert.message}
      </p>
    </div>
  );
}
